"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Copy, KeyRound, LoaderCircle, Plug, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

type PluginPermissions = {
  read?: boolean;
  write?: boolean;
  protected?: boolean;
  manage?: boolean;
};

type RunyiPlugin = {
  plugin_key: string;
  display_name: string;
  description: string;
  category: string;
  auth_strategy: "INTERNAL" | "SUPABASE_SESSION" | "EDGE_SECRET" | "OAUTH" | "MCP";
  visible: boolean;
  supports_chat: boolean;
  supports_work: boolean;
  connection_help?: string | null;
  sort_order: number;
  connected: boolean;
  connection_status: "CONNECTED" | "DISABLED" | "ERROR" | "NOT_CONNECTED";
  account_label?: string | null;
  can_chat: boolean;
  can_work: boolean;
  permissions?: PluginPermissions;
  grant_source?: "OWNER" | "EXPLICIT" | "ROLE_DEFAULT";
};

type PluginCatalogue = {
  user_id?: string;
  role?: string;
  owner_can_manage?: boolean;
  plugins?: RunyiPlugin[];
};

type OwnerConnectorCredential = {
  credential_id: string;
  label: string;
  scopes: string[];
  status: "ACTIVE" | "REVOKED" | "EXPIRED";
  expires_at: string;
  last_used_at?: string | null;
  created_at: string;
  revoked_at?: string | null;
};

type OwnerConnectorCatalogue = {
  mcp_url?: string;
  credentials?: OwnerConnectorCredential[];
};

type IssuedConnector = {
  credential_id: string;
  label: string;
  token: string;
  scopes: string[];
  expires_at: string;
  mcp_url: string;
  shown_once: boolean;
};

type GrantProfile = "READ" | "WRITE" | "FULL";
type GrantMode = "BOTH" | "CHAT" | "WORK";

function permissionLabel(permissions?: PluginPermissions) {
  if (permissions?.manage) return "Full control";
  if (permissions?.protected) return "Protected actions";
  if (permissions?.write) return "Read + write";
  if (permissions?.read) return "Read";
  return "No actions";
}

function connectionLabel(plugin: RunyiPlugin) {
  if (plugin.connection_status === "DISABLED") return "Disabled";
  if (plugin.connection_status === "ERROR") return "Connection error";
  if (plugin.connected) return "Connected";
  return "Available";
}

export function PluginConnectionsPanel({
  supabase,
  isOwner,
}: {
  supabase: SupabaseClient | null;
  isOwner: boolean;
}) {
  const [catalogue, setCatalogue] = useState<PluginCatalogue | null>(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [targetEmail, setTargetEmail] = useState("");
  const [grantPlugin, setGrantPlugin] = useState("github");
  const [grantProfile, setGrantProfile] = useState<GrantProfile>("READ");
  const [grantMode, setGrantMode] = useState<GrantMode>("BOTH");
  const [connectorCatalogue, setConnectorCatalogue] = useState<OwnerConnectorCatalogue | null>(null);
  const [issuedConnector, setIssuedConnector] = useState<IssuedConnector | null>(null);

  const plugins = useMemo(
    () => (catalogue?.plugins ?? []).filter((plugin) => plugin.visible),
    [catalogue],
  );

  const load = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    const [{ data, error }, connectorResult] = await Promise.all([
      supabase.rpc("runyi_plugin_catalogue"),
      isOwner
        ? supabase.rpc("runyi_owner_connector_catalogue")
        : Promise.resolve({ data: null, error: null }),
    ]);
    if (error) {
      setNotice(error.message);
    } else {
      setCatalogue((data ?? null) as PluginCatalogue | null);
    }
    if (connectorResult.error) {
      setNotice(connectorResult.error.message);
    } else if (isOwner) {
      setConnectorCatalogue((connectorResult.data ?? null) as OwnerConnectorCatalogue | null);
    }
    setLoading(false);
  }, [supabase, isOwner]);

  useEffect(() => {
    void load();
  }, [load]);

  async function setConnectionState(plugin: RunyiPlugin) {
    if (!supabase || !isOwner) return;
    const nextStatus = plugin.connection_status === "DISABLED" ? "CONNECTED" : "DISABLED";
    setLoading(true);
    setNotice(null);
    const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
      body: {
        operation: "owner_plugin_connection_set",
        plugin_key: plugin.plugin_key,
        status: nextStatus,
      },
    });
    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);
    setNotice(errorMessage ?? `${plugin.display_name} is now ${nextStatus === "CONNECTED" ? "enabled" : "disabled"} for RUNYI.`);
    if (!errorMessage) await load();
    setLoading(false);
  }

  function profilePermissions(profile: GrantProfile): PluginPermissions {
    if (profile === "FULL") {
      return { read: true, write: true, protected: true };
    }
    if (profile === "WRITE") {
      return { read: true, write: true, protected: false };
    }
    return { read: true, write: false, protected: false };
  }

  async function issueOwnerConnector() {
    if (!supabase || !isOwner) return;
    setLoading(true);
    setNotice(null);
    setIssuedConnector(null);

    const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
      body: {
        operation: "owner_connector_issue",
        label: "Owner ChatGPT",
        expires_at: null,
        allow_break_glass: false,
      },
    });
    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);

    if (errorMessage) {
      setNotice(errorMessage);
    } else {
      setIssuedConnector((data?.connector ?? null) as IssuedConnector | null);
      setNotice("Owner ChatGPT connector credential created. Copy it now; RUNYI stores only its hash.");
      await load();
    }
    setLoading(false);
  }

  async function revokeOwnerConnector(credentialId: string) {
    if (!supabase || !isOwner) return;
    setLoading(true);
    setNotice(null);
    const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
      body: {
        operation: "owner_connector_revoke",
        credential_id: credentialId,
      },
    });
    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);
    setNotice(errorMessage ?? "Owner ChatGPT connector credential revoked.");
    if (!errorMessage) {
      if (issuedConnector?.credential_id === credentialId) setIssuedConnector(null);
      await load();
    }
    setLoading(false);
  }

  async function copyText(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      setNotice(`${label} copied.`);
    } catch {
      setNotice(`Could not copy ${label.toLowerCase()} automatically. Press and hold the field to copy it.`);
    }
  }

  async function saveGrant(active = true) {
    if (!supabase || !isOwner) return;
    const email = targetEmail.trim().toLowerCase();
    if (!email) {
      setNotice("Enter the signed-in RUNYI account email that should receive plugin access.");
      return;
    }

    const allowChat = grantMode === "BOTH" || grantMode === "CHAT";
    const allowWork = grantMode === "BOTH" || grantMode === "WORK";
    setLoading(true);
    setNotice(null);

    const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
      body: {
        operation: "owner_plugin_grant_set",
        email,
        plugin_key: grantPlugin,
        allow_chat: active ? allowChat : false,
        allow_work: active ? allowWork : false,
        permissions: active ? profilePermissions(grantProfile) : {},
        active,
      },
    });
    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);

    setNotice(
      errorMessage
        ? errorMessage
        : active
          ? `Plugin access saved for ${email}.`
          : `Explicit ${grantPlugin} plugin access revoked for ${email}.`,
    );
    if (!errorMessage) await load();
    setLoading(false);
  }

  return (
    <Card className="border-foreground/10 bg-card py-0">
      <CardContent className="space-y-4 p-4 sm:space-y-5 sm:p-7">
        <div className="flex items-start gap-4">
          <div className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Plug className="size-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold">Plugins & Connections</p>
              <Badge className="bg-foreground/8 text-foreground/65">Shared by Chat + Work</Badge>
            </div>
            <p className="mt-1 text-sm leading-6 text-foreground/48">
              RUNYI exposes only plugins that are genuinely connected and permitted for this account. Tokens and API secrets stay on the server and never appear in the Android UI or AI prompt.
            </p>
          </div>
          <Button
            variant="outline"
            size="icon"
            disabled={loading || !supabase}
            onClick={() => void load()}
            className="shrink-0 border-foreground/12 bg-transparent"
            title="Refresh plugins"
          >
            {loading ? <LoaderCircle className="animate-spin" /> : <RefreshCw />}
          </Button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {plugins.map((plugin) => (
            <div key={plugin.plugin_key} className="rounded-2xl border border-foreground/10 bg-[var(--surface-inset)] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold">{plugin.display_name}</p>
                  <p className="mt-1 text-xs leading-5 text-foreground/45">{plugin.description}</p>
                </div>
                <Badge
                  className={
                    plugin.connected
                      ? "bg-emerald-400/10 text-emerald-300"
                      : plugin.connection_status === "DISABLED"
                        ? "bg-amber-300/10 text-amber-200"
                        : "bg-foreground/8 text-foreground/55"
                  }
                >
                  {connectionLabel(plugin)}
                </Badge>
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5">
                <Badge className={plugin.can_chat ? "bg-primary/10 text-primary" : "bg-foreground/6 text-foreground/35"}>
                  Chat {plugin.can_chat ? "✓" : "—"}
                </Badge>
                <Badge className={plugin.can_work ? "bg-primary/10 text-primary" : "bg-foreground/6 text-foreground/35"}>
                  Work {plugin.can_work ? "✓" : "—"}
                </Badge>
                <Badge className="bg-foreground/6 text-foreground/50">{permissionLabel(plugin.permissions)}</Badge>
              </div>

              {isOwner && plugin.account_label && (
                <p className="mt-3 break-words text-[11px] leading-5 text-foreground/40">
                  Connection: {plugin.account_label}
                </p>
              )}

              {!plugin.connected && plugin.connection_status === "NOT_CONNECTED" && plugin.connection_help && (
                <p className="mt-3 text-[11px] leading-5 text-foreground/40">{plugin.connection_help}</p>
              )}

              {isOwner && plugin.connection_status !== "NOT_CONNECTED" && (
                <Button
                  variant="outline"
                  disabled={loading}
                  onClick={() => void setConnectionState(plugin)}
                  className="mt-3 h-9 w-full border-foreground/12 bg-transparent text-xs"
                >
                  {plugin.connection_status === "DISABLED" ? "Enable system connection" : "Disable system connection"}
                </Button>
              )}
            </div>
          ))}
        </div>

        {isOwner && (
          <div className="space-y-4 rounded-2xl border border-foreground/10 bg-[var(--surface-inset)] p-4">
            <div className="flex items-start gap-3">
              <KeyRound className="mt-0.5 size-5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">Owner ChatGPT Management Connector</p>
                <p className="mt-1 text-xs leading-5 text-foreground/45">
                  Private MCP access to the RUNYI Control Tower. Credentials are Owner-only, expire automatically, and are stored by RUNYI only as SHA-256 hashes.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/45">MCP endpoint</p>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={connectorCatalogue?.mcp_url ?? "https://enbisficlrovjuqxjksy.supabase.co/functions/v1/runyi-owner-mcp"}
                  className="h-10 border-foreground/12 bg-card text-xs"
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0 border-foreground/12 bg-transparent"
                  onClick={() => void copyText(
                    connectorCatalogue?.mcp_url ?? "https://enbisficlrovjuqxjksy.supabase.co/functions/v1/runyi-owner-mcp",
                    "MCP endpoint",
                  )}
                >
                  <Copy className="size-4" />
                </Button>
              </div>
            </div>

            {issuedConnector && (
              <div className="space-y-2 rounded-xl border border-amber-400/25 bg-amber-400/[0.06] p-3">
                <p className="text-xs font-semibold text-amber-200">Copy this credential now — it is shown only once.</p>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={issuedConnector.token}
                    className="h-10 border-amber-400/20 bg-card font-mono text-[11px]"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0 border-amber-400/20 bg-transparent"
                    onClick={() => void copyText(issuedConnector.token, "Connector credential")}
                  >
                    <Copy className="size-4" />
                  </Button>
                </div>
                <p className="text-[11px] leading-5 text-foreground/45">
                  Scopes: {issuedConnector.scopes.join(", ")} · Expires {new Date(issuedConnector.expires_at).toLocaleString()}
                </p>
              </div>
            )}

            <Button
              disabled={loading}
              onClick={() => void issueOwnerConnector()}
              className="h-11 w-full bg-primary font-bold text-primary-foreground hover:bg-primary/90"
            >
              Create Owner ChatGPT credential
            </Button>

            {(connectorCatalogue?.credentials ?? []).length > 0 && (
              <div className="space-y-2">
                {(connectorCatalogue?.credentials ?? []).map((credential) => (
                  <div key={credential.credential_id} className="flex items-center justify-between gap-3 rounded-xl border border-foreground/10 bg-card p-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-xs font-semibold">{credential.label}</p>
                        <Badge className={credential.status === "ACTIVE" ? "bg-emerald-400/10 text-emerald-300" : "bg-foreground/8 text-foreground/45"}>
                          {credential.status}
                        </Badge>
                      </div>
                      <p className="mt-1 text-[10px] leading-4 text-foreground/40">
                        Expires {new Date(credential.expires_at).toLocaleString()}
                        {credential.last_used_at ? ` · Last used ${new Date(credential.last_used_at).toLocaleString()}` : ""}
                      </p>
                    </div>
                    {credential.status === "ACTIVE" && (
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={loading}
                        className="shrink-0 border-foreground/12 bg-transparent"
                        onClick={() => void revokeOwnerConnector(credential.credential_id)}
                        title="Revoke connector credential"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {isOwner && (
          <div className="space-y-4 rounded-2xl border border-primary/20 bg-primary/[0.035] p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 size-5 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-semibold">Owner plugin delegation</p>
                <p className="mt-1 text-xs leading-5 text-foreground/45">
                  Grant a joined RUNYI account a specific connected plugin. These grants can expand an Admin or Member beyond role defaults without giving Owner Admin authority.
                </p>
              </div>
            </div>

            <Input
              type="email"
              value={targetEmail}
              onChange={(event) => setTargetEmail(event.target.value)}
              placeholder="user@example.com"
              className="h-11 border-foreground/12 bg-card"
            />

            <div className="grid gap-3 sm:grid-cols-3">
              <label className="space-y-1.5 text-xs font-semibold text-foreground/60">
                Plugin
                <select
                  value={grantPlugin}
                  onChange={(event) => setGrantPlugin(event.target.value)}
                  className="h-11 w-full rounded-md border border-foreground/12 bg-card px-3 text-sm text-foreground outline-none"
                >
                  {plugins.map((plugin) => (
                    <option key={plugin.plugin_key} value={plugin.plugin_key}>
                      {plugin.display_name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-1.5 text-xs font-semibold text-foreground/60">
                Use in
                <select
                  value={grantMode}
                  onChange={(event) => setGrantMode(event.target.value as GrantMode)}
                  className="h-11 w-full rounded-md border border-foreground/12 bg-card px-3 text-sm text-foreground outline-none"
                >
                  <option value="BOTH">Chat + Work</option>
                  <option value="CHAT">Chat only</option>
                  <option value="WORK">Work only</option>
                </select>
              </label>

              <label className="space-y-1.5 text-xs font-semibold text-foreground/60">
                Capability
                <select
                  value={grantProfile}
                  onChange={(event) => setGrantProfile(event.target.value as GrantProfile)}
                  className="h-11 w-full rounded-md border border-foreground/12 bg-card px-3 text-sm text-foreground outline-none"
                >
                  <option value="READ">Read only</option>
                  <option value="WRITE">Read + write</option>
                  <option value="FULL">Protected actions</option>
                </select>
              </label>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <Button
                disabled={loading || !targetEmail.trim()}
                onClick={() => void saveGrant(true)}
                className="h-11 bg-primary font-bold text-primary-foreground hover:bg-primary/90"
              >
                Save plugin access
              </Button>
              <Button
                variant="outline"
                disabled={loading || !targetEmail.trim()}
                onClick={() => void saveGrant(false)}
                className="h-11 border-foreground/12 bg-transparent"
              >
                Revoke explicit grant
              </Button>
            </div>
          </div>
        )}

        {notice && (
          <div className="rounded-xl border border-foreground/10 bg-[var(--surface-inset)] p-3 text-xs leading-5 text-foreground/65">
            {notice}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
